import React, { useEffect, useState } from "react";
import { Form, OverlayTrigger, Tooltip, Button, Modal } from "react-bootstrap";
import { IconButton, Typography, Box } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PersonIcon from "@mui/icons-material/Person";
import RealEstateAgentIcon from '@mui/icons-material/RealEstateAgent';
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getAspectRatio } from "../utils/PreviewUtils";

// Creates an object URL once per Blob and releases it on cleanup.
// Returns a usable image src (object URL, data URL / http string) or null.
const useImageSrc = (value) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (value instanceof Blob) {
      const url = URL.createObjectURL(value);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    }
    setSrc(typeof value === "string" ? value : null);
  }, [value]);

  return src;
};

// Small preview under an image field. Clicking it opens the full image.
const ImageThumb = ({ src, label, onView }) => {
  if (!src) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        component="img"
        src={src}
        alt="thumb"
        onClick={() => onView(src, label)}
        sx={{
          width: "100%",
          height: 44,
          borderRadius: '6px',
          objectFit: 'contain',
          border: '1px solid #e2e8f0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          cursor: "zoom-in",
          display: "block",
        }}
      />
    </Box>
  );
};

// One image field: label row (with View button next to the label), then the
// input/thumbnail passed in as a render-prop. Wrapped in its own component so
// the useImageSrc hook is called once per field (hooks can't run inside .map).
const ImageFieldLabelAndThumb = ({ field, value, isImageSet, onView, children }) => {
  const src = useImageSrc(isImageSet ? value : null);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Form.Label style={{ fontWeight: 600, color: '#475569', fontSize: '0.85rem', margin: 0 }}>
            {field.label}
            {(field.required === "true" || field.required === true) && (
              <span style={{ color: "#ef4444", marginLeft: '4px' }}>*</span>
            )}
          </Form.Label>
          {src && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`view-tooltip-${field.name}`}>View image</Tooltip>}
            >
              <IconButton
                size="small"
                onClick={() => onView(src, field.label)}
                aria-label={`View ${field.label} larger`}
                sx={{ color: '#2f539b', p: 0.5 }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </OverlayTrigger>
          )}
        </Box>
        {field.maxLength && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{`Max ${field.maxLength} characters`}</Tooltip>}
          >
            <IconButton size="small" tabIndex={-1} sx={{ color: '#94a3b8', p: 0.5 }}>
              <HelpOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </OverlayTrigger>
        )}
      </Box>
      {children(src)}
    </>
  );
};

const ReusableForm = ({ selectedTemplateConfig, formFields, onChange, onFocus, handleGeneratePreview, highlightFieldName, fieldErrors = {} }) => {
  const groupLabels = {
    contact: "VARIABLE Fields",
    customer: "FIXED Fields",
  };
  const [validated, setValidated] = useState(false);
  // Image shown in the large preview: { src, label } or null when closed
  const [viewImage, setViewImage] = useState(null);
  const sections = {
    contact: selectedTemplateConfig.contact || [],
    customer: selectedTemplateConfig.customer || [],
  };

  return (
    <Form
      noValidate
      className="premium-form"
      onSubmit={(e) => {
        e.preventDefault();
        setValidated(true);
        const hasErrors = Object.entries(selectedTemplateConfig).some(
          ([group, fields]) =>
            Array.isArray(fields) &&
            fields.some(
              (f) =>
                (f.required === true || f.required === "true") &&
                (!formFields[group]?.[f.name] ||
                  (f.type !== "file" && !formFields[group]?.[f.name]?.toString()?.trim()) ||
                  (f.type === "file" && formFields[group]?.[f.name] === f.defaultValue))
            )
        );
        if (hasErrors) {
          const firstInvalid = document.querySelector(".is-invalid");
          if (firstInvalid) {
            firstInvalid.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstInvalid.focus();
          }
          return;
        }
        handleGeneratePreview();
      }}
    >
      {Object.entries(sections).map(([group, fields]) => (
        <fieldset key={group} style={{ marginBottom: '10px', border: 'none' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            {group === 'contact' ? <PersonIcon sx={{ color: '#2f539b' }} /> : <RealEstateAgentIcon sx={{ color: '#2f539b' }} />}
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1.1rem' }}>
              {groupLabels[group]}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gap: 1 }}>
            {fields.map((field) => {
              const value = formFields[group]?.[field.name];
              const isImageSet =
                value instanceof File ||
                (value &&
                  value !== field.defaultValue &&
                  value.toString().startsWith('data:'));

              return (
                <Form.Group controlId={`form-${field.name}`} key={field.name}>
                  {field.type === "file" ? (
                    <ImageFieldLabelAndThumb
                      field={field}
                      value={value}
                      isImageSet={isImageSet}
                      onView={(src, label) => setViewImage({ src, label })}
                    >
                      {(src) => (
                        <Box sx={{ mb: 0.5 }}>
                          <Form.Control
                            type="file"
                            name={field.name}
                            onChange={(e) => onChange(e, group, field.name, field.type, field.imageSize)}
                            onFocus={() => onFocus(field.variableName || field.name, selectedTemplateConfig.type)}
                            isInvalid={
                              (validated &&
                                (field.required === true || field.required === "true") &&
                                (!value || value === field.defaultValue)) ||
                              !!fieldErrors[`${group}-${field.name}`]
                            }
                            accept="image/*"
                            style={{
                              borderRadius: '8px',
                              padding: '6px 12px',
                              fontSize: '0.85rem',
                              backgroundColor: (field.variableName || field.name) === highlightFieldName ? '#fffbeb' : '#fff',
                              border: fieldErrors[`${group}-${field.name}`] ? '2px solid #ef4444' : ((field.variableName || field.name) === highlightFieldName ? '2px solid #f59e0b' : '1px solid #e2e8f0'),
                              boxShadow: fieldErrors[`${group}-${field.name}`] ? '0 0 0 3px rgba(239, 68, 68, 0.1)' : ((field.variableName || field.name) === highlightFieldName ? '0 0 0 3px rgba(245, 158, 11, 0.1)' : 'none'),
                            }}
                          />
                          {field.imageSize && (
                            <Typography variant="caption" sx={{ color: "#64748b", fontSize: "0.75rem", mt: 0.5, display: "block" }}>
                              <span style={{ fontWeight: 600 }}>Size-</span>
                              {field.imageSize}px,
                              <span style={{ fontWeight: 600 }}> Ratio-</span>
                              {getAspectRatio(field.imageSize)},
                              <span style={{ fontWeight: 600 }}> Res-</span>
                              300dpi
                            </Typography>
                          )}
                          {fieldErrors[`${group}-${field.name}`] && (
                            <Typography variant="caption" sx={{ color: "#ef4444", fontSize: "0.75rem", mt: 0.5, display: "block", fontWeight: 500 }}>
                              {fieldErrors[`${group}-${field.name}`]}
                            </Typography>
                          )}
                          <ImageThumb
                            src={src}
                            label={field.label}
                            onView={(s, label) => setViewImage({ src: s, label })}
                          />
                        </Box>
                      )}
                    </ImageFieldLabelAndThumb>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Form.Label style={{ fontWeight: 600, color: '#475569', fontSize: '0.85rem', margin: 0 }}>
                          {field.label}
                          {(field.required === "true" || field.required === true) && (
                            <span style={{ color: "#ef4444", marginLeft: '4px' }}>*</span>
                          )}
                        </Form.Label>
                        {field.maxLength && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{`Max ${field.maxLength} characters`}</Tooltip>}
                          >
                            <IconButton size="small" tabIndex={-1} sx={{ color: '#94a3b8', p: 0.5 }}>
                              <HelpOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </OverlayTrigger>
                        )}
                      </Box>

                      <Form.Control
                        type={field.type}
                        name={field.name}
                        autoComplete="on"
                        value={value || ""}
                        onChange={(e) => onChange(e, group, field.name, field.type)}
                        onFocus={() => {
                          onFocus(field.variableName || field.name, selectedTemplateConfig?.type);
                        }}
                        maxLength={field.maxLength ? field.maxLength : null}
                        placeholder={field.placeholder ? field.placeholder : ""}
                        isInvalid={
                          validated &&
                          (field.required === true || field.required === "true") &&
                          !value?.trim()
                        }
                        style={{
                          borderRadius: '8px',
                          padding: '8px 12px',
                          fontSize: '0.9rem',
                          backgroundColor: (field.variableName || field.name) === highlightFieldName ? '#fffbeb' : '#fff',
                          border: (field.variableName || field.name) === highlightFieldName ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                          boxShadow: (field.variableName || field.name) === highlightFieldName ? '0 0 0 3px rgba(245, 158, 11, 0.1)' : 'none',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25, minHeight: '1.25rem' }}>
                    {validated && (field.required === true || field.required === "true") && (
                      !value ||
                      (field.type !== "file" && !value?.toString()?.trim()) ||
                      (field.type === "file" && value === field.defaultValue)
                    ) && (
                        <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem', display: 'block', margin: 0 }}>
                          {field.label} is required
                        </Form.Control.Feedback>
                      )}

                    {field.maxLength && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: (value || "").length >= field.maxLength ? '#ef4444' : '#94a3b8',
                          fontWeight: (value || "").length >= field.maxLength ? 700 : 400,
                          ml: 'auto',
                          fontSize: '0.7rem'
                        }}
                      >
                        {(value || "").length}/{field.maxLength}
                      </Typography>
                    )}
                  </Box>
                </Form.Group>
              );
            })}
          </Box>
        </fieldset>
      ))}

      <Button
        variant="primary"
        type="submit"
        className="generate-button"
        style={{
          background: 'var(--primary-gradient)',
          border: 'none',
          padding: '5px 15px 7px',
          fontWeight: 700,
          borderRadius: '8px',
          width: 'fit-content',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          boxShadow: '0 4px 12px rgba(47, 83, 155, 0.2)',
          fontSize: '0.82rem'
        }}
      >
        <VisibilityIcon sx={{ fontSize: 16 }} />
        Preview
      </Button>

      {/* Large image preview (shared by all image fields) */}
      <Modal
        show={!!viewImage}
        onHide={() => setViewImage(null)}
        centered
        size="xl"
        className="view-image-modal"
        backdropClassName="view-image-modal-backdrop"
        style={{ zIndex: 1600 }}
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1e293b" }}>
            {viewImage?.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#f1f5f9",
            padding: 12,
          }}
        >
          {viewImage && (
            <img
              src={viewImage.src}
              alt={viewImage.label || "Selected image"}
              style={{
                maxWidth: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                display: "block",
                // Border drawn with a gap around the image, so the image edge
                // stays visible even when its colour matches the background.
                outline: "2px solid #94a3b8",
                outlineOffset: 6,
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </Form>
  );
};

export default ReusableForm;